import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RegistrationIdentityStep from './RegistrationIdentityStep';
import RegistrationIntro from './RegistrationIntro';
import ConsentStep from './ConsentStep';
import TotpSetupStep from './TotpSetupStep';
import { isWebauthnSupported } from '../auth/webauthnHelpers';
import { navigateAway } from '../lib/tabCloseSuppression';
import './loginNeonRing.css';

// UPDATED 2026-07-22: sso-sms-setup is UNBOUND from bescom-sso-registration
// entirely — Authentik's SMS Authenticator Setup Stage has a confirmed
// internal bug (provider-agnostic "Invalid phone number" validation
// failure) that made phone-only registration impossible. See
// phoneVerification.js (backend) header for the full investigation.
//
// UPDATED 2026-07-28: email is unbound the same way now too (see
// registration.js header) — attributes.pending_email instead of the real
// `email` field, ak-stage-email gone from this flow entirely.
//
// UPDATED 2026-09-18: phone/email verification moved BEFORE the identity
// prompt is ever submitted at all, and is now entirely INLINE on this
// same single page (RegistrationIdentityStep) — the old post-submission
// bescom-phone-pending/bescom-email-pending server-side gate is gone.
// That gate only ever worked because TOTP, mandatory and interactive, was
// guaranteed to be the next stage and naturally paused Authentik's flow
// afterward — it provided no real independent enforcement. See
// registration.js's own header and the project plan for the full
// reasoning (a cloned test flow with TOTP unbound confirmed the account
// could fully activate in Authentik before either channel was verified).
//
// Confirmed stage sequence: ak-stage-prompt (identity, phone/email already
// verified by this point) -> ak-stage-consent -> ak-stage-authenticator-totp
// -> xak-flow-redirect / ak-stage-access-denied -> { done: true } (see
// registration.js).

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
// Same page SetupPasskey.jsx/Login.jsx already use for enrollment — see
// authentik-webauthn-related-origins-ingress.yaml's own comment on why no
// trailing slash. Reused here unchanged: /account/add-webauthn/start-ceremony
// works the same for a freshly-registered session as any other logged-in
// one, since registration's own flow completion already sets the real
// bescom_token cookie before this component ever sees data.done — no
// pending-user bootstrap needed the way login.js's mid-login version does.
const WEBAUTHN_CEREMONY_URL = process.env.REACT_APP_WEBAUTHN_CEREMONY_URL || 'https://auth.bescomapps.com/webauthn-ceremony';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

// Same darker, more transparent glass as Login.jsx, for visual consistency
// between the two auth entry points — see Login.jsx's own card comment.
const card = {
  background: 'rgba(10, 20, 40, 0.38)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '16px',
  padding: '56px 48px',
  width: '100%',
  // Matches Login.jsx's own card — see that file's comment on this exact
  // spot: maxWidth bumped to 596px (500 content + 96px padding) together
  // with boxSizing:'border-box' below so the card's real rendered size
  // doesn't shrink from what it was before box-sizing was fixed.
  maxWidth: '596px',
  boxSizing: 'border-box',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.45)',
};

const textMuted = 'rgba(255, 255, 255, 0.75)';

export default function Register() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Shown every visit, ahead of the real form — fetchChallenge() below
  // still runs immediately in the background so the actual flow session
  // is already warmed up by the time someone clicks through the intro.
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // token present -> registration's own flow already established an
  // authenticated session (see registration.js — covers both a real
  // xak-flow-redirect and its confirmed ak-stage-access-denied false
  // negative); sign straight in exactly like Login.jsx does on its own
  // flow completion. Absent -> the defensive fallback kicked in; fall
  // back to the old "please sign in" prompt rather than a broken nav.
  //
  // Deliberately NOT using data.sessionSyncUrl here, unlike Login.jsx —
  // confirmed that following it (a real top-level redirect through
  // Authentik's sso-session-sync recovery flow) immediately after
  // registration silently wipes attributes.contact_phone we'd just
  // written moments earlier (Authentik appears to run that flow against a
  // stale cached copy of the user record). Cross-app SSO (clicking into
  // Transfer App etc. without a separate login) still needs a live
  // Authentik session eventually though — bescom_needs_session_sync flags
  // that for Dashboard.jsx to pick up on its first safe load instead,
  // once enough real time/user-interaction has passed that the same
  // clobbering risk is no longer in play (see accountUpdate.js's
  // /session-sync-link header for the full reasoning).
  //
  // Without that sessionSyncUrl redirect there's also no more full-page
  // reload happening on the way to /complete-profile — which used to be
  // the (accidental) only thing that refreshed AuthContext's `user` after
  // registration. Client-side nav alone leaves it stale, so
  // CompleteProfile.jsx would see no user and never load hierarchy data
  // until a manual refresh. refreshUser() re-resolves it from the token
  // we just stored, synchronously before navigating away.
  // ADDED (2026-09-18): passkeyOffer holds the completion `data` once
  // registration itself is done and a real session exists, but BEFORE
  // navigating onward — renders the optional "set up a passkey now?"
  // interstitial (see finishRegistration below) instead of leaving
  // straight away. TOTP stays the only MANDATORY factor here; this is
  // purely an additive offer, easy to skip.
  const [passkeyOffer, setPasskeyOffer] = useState(null);
  const [passkeyOfferBusy, setPasskeyOfferBusy] = useState(false);
  // ADDED (2026-09-20): the mandatory Authenticator App / Passkey / SMS
  // Code choice — shown instead of passkeyOffer whenever data.hasMfa is
  // false (see registration.js's respondIfComplete). That's only ever the
  // case on a flow with the TOTP stage unbound (today: the test flow via
  // REGISTRATION_FLOW_SLUG_OVERRIDE — see the project plan's STEP 4). On
  // the current live flow, TOTP already ran as Authentik's own mandatory
  // stage by the time this component ever sees data.done, so hasMfa is
  // always true there and passkeyOffer's existing skippable behavior is
  // unchanged.
  const [mfaChoice, setMfaChoice] = useState(null);
  // Which card is in flight — null | 'totp' | 'passkey' | 'code' — not just whether ANY of them is, so
  // clicking one doesn't flip the OTHER cards' own busy label text too (real bug, seen live 2026-09-22 on
  // ClaimAccount.jsx's identical screen: choosing Passkey made the code card say "Please wait…" too).
  const [mfaChoiceBusy, setMfaChoiceBusy] = useState(null);
  // Which card is mid-flow — 'totp' shows the QR/code sub-screen inline;
  // 'passkey'/'sms' redirect or complete immediately, so they don't need
  // their own sub-state here.
  const [mfaChoiceMode, setMfaChoiceMode] = useState(null);
  const [totpChallenge, setTotpChallenge] = useState(null);
  const [totpLoading, setTotpLoading] = useState(false);

  async function handleCompletion(data) {
    if (data.authenticated) {
      // bescom_token is now set as an httpOnly cookie by the backend's
      // response itself (see AuthContext.js's own header comment) — no
      // client-side storage step needed anymore. data.authenticated is
      // the boolean signal that a real session was established (Phase 3
      // dropped the raw token from the response body).
      if (!data.profileComplete) {
        localStorage.setItem('bescom_needs_profile', '1');
      } else {
        localStorage.removeItem('bescom_needs_profile');
      }
      localStorage.setItem('bescom_needs_session_sync', '1');
      // skipRefresh: this token was just minted a line above — nothing to
      // refresh yet, and /refresh-token would 401 anyway (see
      // AuthContext.js's own comment on resolveUser's skipRefresh param).
      await refreshUser({ skipRefresh: true });
      // These side effects above always run immediately regardless of
      // what happens next — only the actual navigation is deferred, to
      // the interstitial below (or straight through it, if this browser
      // doesn't support WebAuthn at all).
      if (data.hasMfa) {
        if (isWebauthnSupported()) {
          setPasskeyOffer(data);
        } else {
          finishRegistration(data);
        }
      } else {
        // No device confirmed yet at all — this account MUST choose one
        // before it can ever log in again (see login.js's
        // getAccountBlockReason), so unlike passkeyOffer there is no skip.
        setMfaChoice(data);
      }
    } else {
      navigate('/', { state: { justRegistered: true } });
    }
  }

  function finishRegistration(data) {
    navigate(data.profileComplete ? '/dashboard' : '/complete-profile');
  }

  // Mints a ceremony token the SAME way SetupPasskey.jsx does (this is an
  // already-authenticated session by this point), but sends the ceremony
  // page's redirect param straight to wherever registration would have
  // landed anyway (dashboard or complete-profile) — not back through this
  // page — since there's nothing left for Register.jsx itself to do once
  // the person chooses this path.
  async function handleSetupPasskey() {
    setPasskeyOfferBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-webauthn/start-ceremony`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.token) {
        setError(data.error || 'Could not start passkey setup. Please try again, or skip for now.');
        setPasskeyOfferBusy(false);
        return;
      }
      const destination = passkeyOffer.profileComplete ? '/dashboard' : '/complete-profile';
      const redirectBack = encodeURIComponent(window.location.origin + destination);
      navigateAway(`${WEBAUTHN_CEREMONY_URL}?token=${encodeURIComponent(data.token)}&redirect=${redirectBack}`);
    } catch (e) {
      setError('Could not reach the account service. Please try again, or skip for now.');
      setPasskeyOfferBusy(false);
    }
  }

  // Mandatory choice handlers — mirrors handleSetupPasskey's own ceremony
  // redirect for the Passkey card; the other two complete inline.
  async function handleChoosePasskey() {
    setMfaChoiceBusy('passkey');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-webauthn/start-ceremony`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.token) {
        setError(data.error || 'Could not start passkey setup. Please try again.');
        setMfaChoiceBusy(null);
        return;
      }
      const destination = mfaChoice.profileComplete ? '/dashboard' : '/complete-profile';
      const redirectBack = encodeURIComponent(window.location.origin + destination);
      navigateAway(`${WEBAUTHN_CEREMONY_URL}?token=${encodeURIComponent(data.token)}&redirect=${redirectBack}`);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setMfaChoiceBusy(null);
    }
  }

  async function handleStartTotpChoice() {
    setMfaChoiceBusy('totp');
    setMfaChoiceMode('totp');
    setTotpLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/setup-totp/challenge`, { credentials: 'include' });
      const data = await res.json();
      if (!data.raw?.config_url) {
        setError(data.error || 'Could not start authenticator setup. Please try again.');
        setMfaChoiceMode(null);
        setMfaChoiceBusy(null);
        return;
      }
      setTotpChallenge(data);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setMfaChoiceMode(null);
      setMfaChoiceBusy(null);
    } finally {
      setTotpLoading(false);
    }
  }

  async function handleConfirmTotpChoice({ code }) {
    setTotpLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/setup-totp/step`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      if (data.done) {
        // CONFIRMED BUG (2026-09-20): finishRegistration() alone just
        // navigate()s — client-side, no page reload — while AuthContext's
        // `user` (and its needsMfaSetup) was last resolved BEFORE this
        // choice was made, back when handleCompletion() first called
        // refreshUser(). Without re-resolving it here, the brand new
        // AccountCompletionGuard (see App.js) sees a STALE
        // needsMfaSetup:true on the very next render and immediately
        // bounces to /setup-mfa — showing this exact screen a confusing
        // second time right after successfully finishing it. Re-resolve
        // first so the guard sees the truth.
        await refreshUser();
        finishRegistration(mfaChoice);
        return;
      }
      // A wrong code re-shows the same TOTP stage, not a redirect — same
      // {errors} shape used everywhere else this pattern appears.
      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Incorrect code. Please try again.');
        return;
      }
      setError('Unexpected response. Please try again.');
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
    } finally {
      setTotpLoading(false);
    }
  }

  // One-time code sign-in (backend lib/attributeMfa.js): the code goes to the
  // phone if one was given, otherwise the email — both were already verified
  // before the account existed, so choosing it is just one attribute PATCH.
  async function handleChooseCode() {
    setMfaChoiceBusy('code');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/set-code-mfa`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not set up code sign-in. Please try again.');
        setMfaChoiceBusy(null);
        return;
      }
      // Refresh `user` before navigating, or the guard immediately sends
      // this same person right back to /setup-mfa (same as the TOTP choice).
      await refreshUser();
      finishRegistration(mfaChoice);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setMfaChoiceBusy(null);
    }
  }

    async function fetchChallenge() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/register/challenge`, { credentials: 'include' });
      const data = await res.json();
      if (data.done) {
        handleCompletion(data);
        return;
      }
      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the registration service.');
    } finally {
      setLoading(false);
    }
  }

  // Lets the person back out of an unrecognized/stuck stage after the
  // identity prompt has already been submitted — Authentik's flow only
  // moves forward, never back, so there's no other way to retry from
  // scratch. Clears the tracked flow session server-side (registration.js's
  // /cancel) and resets local state, so the next fetchChallenge() genuinely
  // starts over from the identity prompt.
  async function handleRegistrationCancel() {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/auth/register/cancel`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      // best-effort — fetchChallenge below will just resume the old flow
      // if this didn't reach the backend, no worse than today
    }
    setChallenge(null);
    setError(null);
    fetchChallenge();
  }

  async function submitStep(body) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/register/step`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.done) {
        handleCompletion(data);
        return;
      }

      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Something went wrong. Please try again.');
        setChallenge(data);
        // A rejected identity form re-shows the same prompt: keep what was
        // typed (username, verified phone/email) and clear only the
        // passwords. Wiping everything threw away verified contacts the
        // server still remembers, forcing a re-verify over a typo.
        if (data.stage === 'ak-stage-prompt') {
          setValues((prev) => ({ ...prev, password: '', password_repeat: '' }));
        } else {
          setValues({});
        }
        return;
      }

      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the registration service.');
    } finally {
      setLoading(false);
    }
  }

  const knownStages = [
    'ak-stage-prompt',
    'ak-stage-consent',
    'ak-stage-authenticator-totp',
  ];
  const isUnknownStage = challenge && !knownStages.includes(challenge.stage);

  return (
    <div style={page}>
      <div style={{ position: 'relative', width: '100%', maxWidth: '596px' }}>
        <div className="loginNeonGlow" />
        <div className="loginNeonRingStripTop" />
        <div className="loginNeonRingStripBottom" />
        <div className="loginNeonRingStripLeft" />
        <div className="loginNeonRingStripRight" />
        <div className="loginNeonRingCorner loginNeonRingCornerTL" />
        <div className="loginNeonRingCorner loginNeonRingCornerTR" />
        <div className="loginNeonRingCorner loginNeonRingCornerBL" />
        <div className="loginNeonRingCorner loginNeonRingCornerBR" />
      <div style={{ ...card, position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: '#f8f9fa', border: '1px solid #dee2e6', color: '#495057',
              fontSize: '13px', cursor: 'pointer', padding: '8px 16px',
              borderRadius: '4px', display: 'flex',
              alignItems: 'center', gap: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#e9ecef'}
            onMouseOut={e => e.currentTarget.style.background = '#f8f9fa'}
          >
            ← Home
          </button>

          {showIntro && (
            <button
              onClick={() => setShowIntro(false)}
              style={{
                background: '#f8f9fa', border: '1px solid #dee2e6', color: '#495057',
                fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '8px 16px',
                borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e9ecef'}
              onMouseOut={e => e.currentTarget.style.background = '#f8f9fa'}
            >
              Skip
            </button>
          )}
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#D4A94C', margin: '0 0 8px' }}>
            {showIntro ? 'Steps to create your BESCOM account' : 'Create your BESCOM account'}
          </h1>
          {!showIntro && (
            <p style={{ fontSize: '14px', color: textMuted, margin: 0 }}>
              {challenge?.title || 'Registration'}
            </p>
          )}
        </div>

        {showIntro ? (
          <RegistrationIntro onFinish={() => setShowIntro(false)} />
        ) : (
        <>
        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {loading && !challenge && (
          <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px' }}>Loading…</p>
        )}

        {passkeyOffer ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '32px', margin: '0 0 8px' }}>🎉</p>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: '0 0 8px' }}>
              Account created!
            </h2>
            <p style={{ fontSize: '14px', color: textMuted, margin: '0 0 24px', lineHeight: 1.5 }}>
              Want to also set up a Passkey now? Sign in faster next time using your
              fingerprint, face, or device PIN — your authenticator app still works either way.
            </p>
            <button
              type="button"
              onClick={handleSetupPasskey}
              disabled={passkeyOfferBusy}
              style={{
                width: '100%', padding: '14px',
                background: passkeyOfferBusy ? '#9BD3F0' : 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '16px', fontWeight: '600', cursor: passkeyOfferBusy ? 'not-allowed' : 'pointer',
              }}
            >
              {passkeyOfferBusy ? 'Redirecting…' : '🔑 Set up a Passkey'}
            </button>
            <button
              type="button"
              onClick={() => finishRegistration(passkeyOffer)}
              disabled={passkeyOfferBusy}
              style={{
                width: '100%', marginTop: '10px', padding: '12px',
                background: 'none', border: '1px solid rgba(255,255,255,0.35)', color: textMuted,
                borderRadius: '10px', fontSize: '14px', fontWeight: '500',
                cursor: passkeyOfferBusy ? 'not-allowed' : 'pointer', opacity: passkeyOfferBusy ? 0.6 : 1,
              }}
            >
              Skip for now
            </button>
          </div>
        ) : mfaChoice ? (
          <div style={{ textAlign: 'center' }}>
            {mfaChoiceMode === 'totp' ? (
              totpChallenge?.raw?.config_url ? (
                <>
                  <TotpSetupStep
                    configUrl={totpChallenge.raw.config_url}
                    loading={totpLoading}
                    onSubmit={handleConfirmTotpChoice}
                    dark
                    mandatory
                  />
                  <button
                    type="button"
                    onClick={() => { setMfaChoiceMode(null); setMfaChoiceBusy(null); setTotpChallenge(null); setError(null); }}
                    disabled={totpLoading}
                    style={{
                      width: '100%', marginTop: '10px', padding: '12px',
                      background: 'none', border: '1px solid rgba(255,255,255,0.35)', color: textMuted,
                      borderRadius: '10px', fontSize: '14px', fontWeight: '500',
                      cursor: totpLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Choose a different method
                  </button>
                </>
              ) : (
                <p style={{ color: textMuted, fontSize: '14px' }}>Loading…</p>
              )
            ) : (
              <>
                <p style={{ fontSize: '32px', margin: '0 0 8px' }}>🎉</p>
                <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: '0 0 8px' }}>
                  Account created!
                </h2>
                <p style={{ fontSize: '14px', color: textMuted, margin: '0 0 24px', lineHeight: 1.5 }}>
                  Choose how you'll sign in — you'll need this every time, so pick whichever is easiest for you.
                </p>
                <button
                  type="button"
                  onClick={handleStartTotpChoice}
                  disabled={!!mfaChoiceBusy}
                  style={{
                    width: '100%', padding: '14px', marginBottom: '10px',
                    background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                    color: '#fff', border: 'none', borderRadius: '10px',
                    fontSize: '16px', fontWeight: '600', cursor: mfaChoiceBusy ? 'not-allowed' : 'pointer',
                    opacity: mfaChoiceBusy ? 0.6 : 1,
                  }}
                >
                  📱 Use an Authenticator App
                </button>
                {isWebauthnSupported() && (
                  <button
                    type="button"
                    onClick={handleChoosePasskey}
                    disabled={!!mfaChoiceBusy}
                    style={{
                      width: '100%', padding: '14px', marginBottom: '10px',
                      background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                      color: '#fff', border: 'none', borderRadius: '10px',
                      fontSize: '16px', fontWeight: '600', cursor: mfaChoiceBusy ? 'not-allowed' : 'pointer',
                      opacity: mfaChoiceBusy ? 0.6 : 1,
                    }}
                  >
                    {mfaChoiceBusy === 'passkey' ? 'Redirecting…' : '🔑 Use a Passkey'}
                  </button>
                )}
                {(() => {
                  const hasPhone = /^\d{10}$/.test((values.phone || '').trim());
                  const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((values['attributes.pending_email'] || '').trim());
                  if (!hasPhone && !hasEmail) return null;
                  return (
                    <button
                      type="button"
                      onClick={handleChooseCode}
                      disabled={!!mfaChoiceBusy}
                      style={{
                        width: '100%', padding: '14px',
                        background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                        color: '#fff', border: 'none', borderRadius: '10px',
                        fontSize: '16px', fontWeight: '600', cursor: mfaChoiceBusy ? 'not-allowed' : 'pointer',
                        opacity: mfaChoiceBusy ? 0.6 : 1,
                      }}
                    >
                      {mfaChoiceBusy === 'code' ? 'Please wait…' : '🔐 Phone/Email OTP'}
                    </button>
                  );
                })()}
              </>
            )}
          </div>
        ) : (
          <>
            {challenge?.stage === 'ak-stage-prompt' && (
              <RegistrationIdentityStep
                fields={challenge.raw.fields}
                values={values}
                setValues={setValues}
                loading={loading}
                onSubmit={() => submitStep(values)}
                dark
              />
            )}

            {challenge?.stage === 'ak-stage-consent' && (
              <ConsentStep
                headerText={challenge.raw.header_text}
                token={challenge.raw.token}
                loading={loading}
                onSubmit={submitStep}
                dark
              />
            )}

            {challenge?.stage === 'ak-stage-authenticator-totp' && (
              <TotpSetupStep
                configUrl={challenge.raw.config_url}
                pendingUser={challenge.raw.pending_user}
                avatar={challenge.raw.pending_user_avatar}
                loading={loading}
                onSubmit={submitStep}
                dark
                mandatory
              />
            )}

            {isUnknownStage && (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: textMuted, marginBottom: '16px' }}>
                  This step ({challenge.stage}) isn't supported yet.
                </p>
                <button onClick={fetchChallenge} style={{
                  background: 'none', border: '1px solid #009FE3', color: '#009FE3',
                  padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', marginRight: '10px',
                }}>
                  Refresh
                </button>
                <button onClick={handleRegistrationCancel} style={{
                  background: 'none', border: '1px solid rgba(255,255,255,0.35)', color: textMuted,
                  padding: '10px 20px', borderRadius: '8px', cursor: 'pointer',
                }}>
                  Start Over
                </button>
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>
      </div>
    </div>
  );
}
